import { useState } from 'react'
import { Link } from 'react-router-dom'
import './Billing.css'

// Mock data
const billingData = {
  period: {
    start: 'Feb 1, 2026',
    end: 'Feb 28, 2026',
    nextPayment: 'March 1, 2026'
  },
  paymentMethod: {
    type: 'visa',
    last4: '4242',
    expiry: '12/27'
  },
  currentMonth: {
    platform: { name: 'Hubthrill Business Plan', amount: 29.00 },
    modules: [
      {
        name: 'Payments (Chekkouts)',
        items: [
          { name: 'Pro Plan base', amount: 49.00 },
          { name: 'Transaction fees (estimated)', amount: 142.00, estimated: true, detail: '312 transactions × 0.5% avg' }
        ]
      },
      {
        name: 'Accounting (Keeply)',
        items: [
          { name: 'Standard Plan', amount: 19.00 }
        ]
      }
    ],
    estimatedTotal: 239.00
  },
  invoices: [
    { id: 'INV-2026-01', period: 'January 2026', amount: 218.42, status: 'paid', date: 'Jan 1, 2026' },
    { id: 'INV-2025-12', period: 'December 2025', amount: 205.91, status: 'paid', date: 'Dec 1, 2025' },
    { id: 'INV-2025-11', period: 'November 2025', amount: 198.50, status: 'paid', date: 'Nov 1, 2025' },
    { id: 'INV-2025-10', period: 'October 2025', amount: 192.00, status: 'paid', date: 'Oct 1, 2025' },
    { id: 'INV-2025-09', period: 'September 2025', amount: 185.22, status: 'paid', date: 'Sep 1, 2025' }
  ],
  usage: {
    payments: {
      transactions: 312,
      volume: '$48,291.00',
      fees: '$142.00'
    },
    infrastructure: {
      compute: '0 hours',
      bandwidth: '0 GB',
      storage: '0 GB'
    }
  }
}

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'For solo operators',
    price: 0,
    features: ['1 user', 'Basic support', 'Email support only'],
    current: false
  },
  {
    id: 'business',
    name: 'Business',
    description: 'For small teams',
    price: 29,
    features: ['Up to 10 users', 'Priority support', 'Audit logs', 'API access'],
    current: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For larger organizations',
    price: null,
    features: ['Unlimited users', 'Dedicated support', 'SLA guarantee', 'Custom integrations'],
    current: false
  }
]

function Billing() {
  const [activeTab, setActiveTab] = useState('overview')
  const [showPlanModal, setShowPlanModal] = useState(false)

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="billing-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing</h1>
          <p className="page-subtitle">Manage your subscription and payment methods</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="billing-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button 
          className={`tab-button ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          Usage
        </button>
        <button 
          className={`tab-button ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
        >
          Plans
        </button>
      </div>

      {/* Tab Content */}
      <div className="billing-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="billing-overview">
            {/* Billing Info Card */}
            <div className="billing-card">
              <div className="card-header">
                <h2 className="card-title">Current Billing Period</h2>
              </div>
              <div className="billing-info">
                <div className="billing-info-row">
                  <span className="info-label">Period</span>
                  <span className="info-value">{billingData.period.start} – {billingData.period.end}</span>
                </div>
                <div className="billing-info-row">
                  <span className="info-label">Next payment</span>
                  <span className="info-value">{billingData.period.nextPayment}</span>
                </div>
                <div className="billing-info-row">
                  <span className="info-label">Payment method</span>
                  <div className="payment-method">
                    <div className="card-icon">
                      <svg width="32" height="20" viewBox="0 0 32 20" fill="none">
                        <rect width="32" height="20" rx="3" fill="#1A1F71"/>
                        <path d="M12 14L13.5 6H15.5L14 14H12Z" fill="#FFFFFF"/>
                        <path d="M19.5 6.2C19 6 18.3 6 17.5 6C15.5 6 14 7 14 8.5C14 9.7 15 10.3 15.8 10.7C16.6 11.1 16.9 11.4 16.9 11.8C16.9 12.4 16.2 12.6 15.5 12.6C14.6 12.6 14.1 12.5 13.3 12.1L13 12L12.7 14C13.3 14.3 14.3 14.5 15.4 14.5C17.6 14.5 19 13.5 19 11.9C19 10.9 18.4 10.2 17.2 9.6C16.5 9.2 16 9 16 8.5C16 8.1 16.4 7.6 17.4 7.6C18.2 7.6 18.7 7.8 19.1 8L19.4 8.1L19.5 6.2Z" fill="#FFFFFF"/>
                        <path d="M22.5 6H21C20.5 6 20.1 6.1 19.9 6.6L17 14H19.2L19.6 12.8H22.3L22.5 14H24.5L22.5 6ZM20.2 11.2L21.2 8.4L21.8 11.2H20.2Z" fill="#FFFFFF"/>
                        <path d="M10.5 6L8.5 11.5L8.3 10.5C7.9 9.2 6.7 7.8 5.4 7.1L7.3 14H9.5L12.8 6H10.5Z" fill="#FFFFFF"/>
                        <path d="M7 6H3.5L3.5 6.2C6 6.8 7.7 8.3 8.3 10.5L7.6 6.7C7.5 6.2 7.1 6 6.6 6H7Z" fill="#FAA61A"/>
                      </svg>
                    </div>
                    <span>Visa ending in {billingData.paymentMethod.last4}</span>
                    <button className="link-button">Change</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Month Breakdown */}
            <div className="billing-card">
              <div className="card-header">
                <h2 className="card-title">Current Month Breakdown</h2>
                <span className="estimate-badge">Estimated</span>
              </div>
              
              <div className="breakdown-list">
                {/* Platform */}
                <div className="breakdown-item">
                  <div className="breakdown-main">
                    <span className="breakdown-name">Platform Base</span>
                    <span className="breakdown-amount">{formatCurrency(billingData.currentMonth.platform.amount)}</span>
                  </div>
                  <div className="breakdown-detail">
                    <span>{billingData.currentMonth.platform.name}</span>
                  </div>
                </div>

                {/* Modules */}
                {billingData.currentMonth.modules.map((module, idx) => (
                  <div key={idx} className="breakdown-item module">
                    <div className="breakdown-main">
                      <span className="breakdown-name">{module.name}</span>
                      <span className="breakdown-amount">
                        {formatCurrency(module.items.reduce((sum, item) => sum + item.amount, 0))}
                      </span>
                    </div>
                    {module.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="breakdown-detail">
                        <span className="detail-name">
                          {item.name}
                          {item.estimated && <span className="estimated-tag">~</span>}
                        </span>
                        <span className="detail-amount">{formatCurrency(item.amount)}</span>
                        {item.detail && <span className="detail-info">{item.detail}</span>}
                      </div>
                    ))}
                  </div>
                ))}

                {/* Total */}
                <div className="breakdown-total">
                  <span className="total-label">Estimated Total</span>
                  <span className="total-amount">{formatCurrency(billingData.currentMonth.estimatedTotal)}</span>
                </div>
              </div>

              <p className="breakdown-note">
                * Transaction fees and usage charges finalized at billing
              </p>
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="invoices-section">
            <div className="billing-card">
              <div className="card-header">
                <h2 className="card-title">Invoice History</h2>
              </div>
              
              <div className="invoices-table">
                <div className="table-header">
                  <span>Invoice</span>
                  <span>Period</span>
                  <span>Amount</span>
                  <span>Status</span>
                  <span></span>
                </div>
                {billingData.invoices.map(invoice => (
                  <div key={invoice.id} className="table-row">
                    <span className="invoice-id">{invoice.id}</span>
                    <span className="invoice-period">{invoice.period}</span>
                    <span className="invoice-amount">{formatCurrency(invoice.amount)}</span>
                    <span className={`invoice-status ${invoice.status}`}>
                      {invoice.status === 'paid' ? '✓ Paid' : invoice.status}
                    </span>
                    <button className="download-button">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="usage-section">
            <div className="usage-grid">
              {/* Payments Usage */}
              <div className="billing-card">
                <div className="card-header">
                  <h2 className="card-title">
                    <span className="module-dot payments"></span>
                    Payments Usage
                  </h2>
                  <span className="period-badge">This month</span>
                </div>
                <div className="usage-stats">
                  <div className="usage-stat">
                    <span className="stat-value">{billingData.usage.payments.transactions}</span>
                    <span className="stat-label">Transactions</span>
                  </div>
                  <div className="usage-stat">
                    <span className="stat-value">{billingData.usage.payments.volume}</span>
                    <span className="stat-label">Volume processed</span>
                  </div>
                  <div className="usage-stat">
                    <span className="stat-value">{billingData.usage.payments.fees}</span>
                    <span className="stat-label">Estimated fees</span>
                  </div>
                </div>
                <div className="usage-progress">
                  <div className="progress-header">
                    <span>Transaction volume</span>
                    <span>312 / unlimited</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill payments" style={{ width: '45%' }}></div>
                  </div>
                </div>
              </div>

              {/* Infrastructure Usage */}
              <div className="billing-card">
                <div className="card-header">
                  <h2 className="card-title">
                    <span className="module-dot infrastructure"></span>
                    Infrastructure Usage
                  </h2>
                  <span className="status-badge inactive">Not Active</span>
                </div>
                <div className="usage-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="2" width="20" height="8" rx="2"/>
                    <rect x="2" y="14" width="20" height="8" rx="2"/>
                    <line x1="6" y1="6" x2="6.01" y2="6"/>
                    <line x1="6" y1="18" x2="6.01" y2="18"/>
                  </svg>
                  <p>Infrastructure module is not active</p>
                  <button className="btn btn-primary btn-sm">Activate Namelyft</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="plans-section">
            <div className="plans-header">
              <h2>Platform Plans</h2>
              <p>Your platform plan determines team size and support level. Module pricing is separate.</p>
            </div>
            
            <div className="plans-grid">
              {plans.map(plan => (
                <div key={plan.id} className={`plan-card ${plan.current ? 'current' : ''}`}>
                  {plan.current && <span className="current-badge">Current Plan</span>}
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                  <div className="plan-price">
                    {plan.price !== null ? (
                      <>
                        <span className="price-amount">${plan.price}</span>
                        <span className="price-period">/month</span>
                      </>
                    ) : (
                      <span className="price-custom">Custom pricing</span>
                    )}
                  </div>
                  <ul className="plan-features">
                    {plan.features.map((feature, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {plan.current ? (
                    <button className="btn btn-secondary" disabled>Current plan</button>
                  ) : plan.price !== null ? (
                    <button className="btn btn-primary">
                      {plan.price === 0 ? 'Downgrade' : 'Upgrade'}
                    </button>
                  ) : (
                    <button className="btn btn-secondary">Contact sales</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Billing
